/**
 * Every catalog namespace the frontend ships, in `src/locales/<code>/<ns>.json`.
 *
 * `common` holds only what three or more surfaces share (actions, states,
 * the language banner). Every other key lives in ITS surface's namespace.
 * L6 (shell externalisation, #2109 R1) appends its namespaces here — this is
 * the one file under `src/i18n/` that lane edits.
 */
// `nav` (every nav label, group label, tab slot and keyed eyebrow), `auth`
// (Login, ForgotPassword, ResetPassword, MagicLink, MfaVerify) and `shell`
// (Header, Sidebar, TRFerSidebar, LegalStrip, BottomNav, NotFound,
// ImpersonationBanner, UnreadsBell, HelpFAB) are L6's. `settings` is L4's and
// joins this list when `src/locales/en/settings.json` lands (the glob in
// `index.ts` and `src/test/i18n.ts` throws on a listed namespace with no file).
// `forms` (the family form renderer's language lines and load states) and
// `validation` (the four-tier validation catalog) are #2109 R2 lane L3's.
// #2109 R3 (parent pages) L0 adds one namespace per surface, each created as
// an en skeleton (`{"_provenance": …}`) so the glob never throws; the lane
// that owns the surface fills it (plan 2026-09-28 "Lane map"). After R3 L0 no
// lane edits this list.
export const NAMESPACES = [
  'common', 'nav', 'auth', 'shell', 'settings', 'forms', 'validation',
  // R3 L1
  'dashboard', 'companion', 'onboarding', 'memorial',
  // R3 L2
  'tasks', 'calendar',
  // R3 L3
  'health',
  // R3 L4
  'grants', 'money', 'applications',
  // R3 L5a (`core.*`) + L5b
  'chat',
  // R3 L6
  'community', 'circle', 'help',
  // R3 L7
  'profile', 'legal', 'consent', 'crisis', 'notifications',
  // R3 L8
  'landing', 'feedback',
  // R3 L9
  'copy',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NAMESPACE: Namespace = 'common';

/**
 * #2507: the namespaces the app chrome needs before a language may switch:
 * header, nav, language switcher, banners, the form wizard's frame and its
 * validation lines. `i18n.changeLanguage` waits for these only (they are the
 * init `ns`). Every other namespace downloads at the same time and renders as
 * it lands, so a stalled page chunk never holds the whole UI in English.
 *
 * #2885: `crisis`, `legal` and `copy` wait too. They are small, and the crisis
 * callout, the legal pages and the program copy slots must never paint in
 * English for a family reading another language.
 */
export const SHELL_NAMESPACES = [
  'common', 'nav', 'shell', 'settings', 'forms', 'validation', 'onboarding',
  'crisis', 'legal', 'copy',
] as const satisfies readonly Namespace[];
